import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Col, Row, Table } from 'antd';
import { CaretRightOutlined, TableOutlined } from '@ant-design/icons';
import cx from 'classnames';
import { IGetApiType } from '../../REST';
import { ICompaniesHierarchyTable } from '../../interfaces';
import { useCompaniesHierarchyIsHaveChildren, useCompaniesHierarchyTable, useUiIsLoading } from '../../hooks';
import { abortSelectedFetch, cleanCompaniesStore, getCompaniesHierarchyList } from '../../middleware';
import { tF, tP } from '../../translate';
import { Catcher, GoPageButton } from '../../Components';
import '../../customStyle/table.less';
import Styles from '../styles/m_viewStyles.less';

type IExpandIcons = {
    record: ICompaniesHierarchyTable;
};

export const CompaniesHierarchy: React.FC = observer(() => {
    const [expandedRowKeys, setExpandedRowKeys] = useState<Array<string>>([]);
    const isLoading = useUiIsLoading();
    const companiesHierarchyList = useCompaniesHierarchyTable();
    const isHaveChildren = useCompaniesHierarchyIsHaveChildren();
    const usedApiTypes: IGetApiType[] = ['GET_COMPANIES_HIERARCHY'];

    useEffect(() => {
        getCompaniesHierarchyList({});
        return (): void => {
            abortSelectedFetch(usedApiTypes);
            cleanCompaniesStore();
        };
    }, []);

    const tableColumns = [
        {
            title: tF('Name'),
            dataIndex: 'name',
        },
        {
            title: tF('Balance'),
            dataIndex: 'balance',
            className: Styles.columnRight,
        },
        {
            title: tF('Payment type'),
            dataIndex: 'paymentType',
            className: Styles.columnRight,
        },
        {
            title: tF('Cost'),
            dataIndex: 'costList',
            className: Styles.columnRight,
        },
        {
            title: tF('Revenue'),
            dataIndex: 'revenueList',
            className: Styles.columnRight,
        },
        {
            title: tF('Listed Cards'),
            dataIndex: 'cardsCount',
            className: Styles.columnRight,
        },
        {
            title: tF('Companies Count'),
            dataIndex: 'childrenCount',
            className: Styles.columnRight,
        },
    ];

    const expandRow = async (key: string): Promise<void> => {
        if (!isHaveChildren.findChildren(key)) {
            await getCompaniesHierarchyList({ parentId: key });
        }
        setExpandedRowKeys([...expandedRowKeys, key]);
    };
    const collapseRow = (key: string): void => {
        setExpandedRowKeys(expandedRowKeys.filter((r) => r !== key));
    };
    const expandIcon = ({ record }: IExpandIcons): JSX.Element => {
        return !record.childrenCount ? (
            <span className={Styles.squareIcon}>■</span>
        ) : expandedRowKeys.includes(record.key) ? (
            <CaretRightOutlined
                className={cx(Styles.collapseIcon, Styles.collapseIconRotate)}
                onClick={(): void => collapseRow(record.key)}
            />
        ) : (
            <CaretRightOutlined className={Styles.collapseIcon} onClick={(): Promise<void> => expandRow(record.key)} />
        );
    };

    return (
        <Catcher>
            <div className={Styles.main}>
                <div className={Styles.content}>
                    <Row className={Styles.header}>
                        <Col xs={24} className={Styles.title}>
                            <GoPageButton
                                componentName={'CompaniesList'}
                                icon={<TableOutlined />}
                                title={'Table View'}
                            />
                            {tP('Companies by Hierarchy')}
                        </Col>
                    </Row>
                    <div className={Styles.tablePlace}>
                        <Table
                            dataSource={companiesHierarchyList}
                            columns={tableColumns}
                            pagination={false}
                            className={'table-theme'}
                            size={'middle'}
                            loading={isLoading}
                            expandable={{ expandIcon, expandedRowKeys }}
                        />
                    </div>
                </div>
            </div>
        </Catcher>
    );
});
